import { isOtherCompositionBucket } from '@revelio/core/composition-buckets';
import type { IcicleNode } from '@revelio/replots';

import {
  DataType,
  type LevelDimension,
  type TableData,
  type TableDimension,
} from '../../DataProvider/types';
import {
  getTableDimensionValue,
  getTableValue,
  groupTableRows,
} from '../../DataProvider/utils/table-data';
import {
  findLadderLevel,
  isLevelDimension,
  isSublevelOf,
  taxonomyOf,
  taxonomyOfLevel,
} from '../../DataResolver/taxonomy-levels';
import { getDrilldownBreakdownLevel } from '../../DataResolver/use-composition-v4-drilldown';
import type { TreemapTaxonomyCoverage } from '../../PageConfigProvider/types/item';
import { formatColumnName } from '../chart-menu/data-download/converters/utils';
import {
  MAX_PARENT_GROUPS,
  taxonomyCoverageMaxGroups,
} from '../utils/buildNestedTreemapData';
import {
  type IcicleData,
  type IcicleDrillStep,
  type IcicleNodeMeta,
  promoteLoneTopLevelNode,
} from './icicle-data';

export type TableIcicleEntity = {
  id: string;
  name: string;
  level: LevelDimension | undefined;
};

type IcicleRoot = { level: LevelDimension | undefined; id: string };

type IcicleCell = { id: string; name: string; count: number; total: number };

type IcicleGroup = IcicleCell & { cells: IcicleCell[] };

type IcicleView = {
  rootColumn: { cell: IcicleCell; level: TableDimension } | undefined;
  groups: IcicleGroup[];
  groupLevel: TableDimension | undefined;
  cellLevel: TableDimension | undefined;
};

type Remainder = { weight: number; total: number; count: number };

const DEFAULT_MAX_CELLS = 50;

const byTotal = (a: IcicleCell, b: IcicleCell) => b.total - a.total;

const sumTotals = (cells: readonly IcicleCell[]) =>
  cells.reduce((sum, cell) => sum + cell.total, 0);

const sumCounts = (cells: readonly IcicleCell[]) =>
  cells.reduce((sum, cell) => sum + cell.count, 0);

const hasRemainder = (remainder: number, total: number) =>
  remainder > Math.max(Math.abs(total), 1) * 1e-9;

const groupsOf = (
  table: TableData,
  rows: readonly number[],
  level: TableDimension,
  sublevel: TableDimension | undefined
): IcicleGroup[] => {
  const groups = new Map<string, IcicleGroup & { subtotal: boolean }>();
  for (const row of rows) {
    const value = getTableDimensionValue(table, level, row);
    if (value === undefined || isOtherCompositionBucket(value.id)) continue;
    const group = groups.get(value.id) ?? {
      id: value.id,
      name: value.label,
      count: 0,
      total: 0,
      cells: [],
      subtotal: false,
    };
    groups.set(value.id, group);
    const count = getTableValue(table, 'headcount', row) ?? 0;
    const total = getTableValue(table, 'share_of_headcount', row) ?? 0;
    const cell =
      sublevel === undefined
        ? undefined
        : getTableDimensionValue(table, sublevel, row);
    if (sublevel !== undefined && cell === undefined) {
      Object.assign(group, { count, total, subtotal: true });
      continue;
    }
    if (!group.subtotal) {
      group.count += count;
      group.total += total;
    }
    if (cell !== undefined && !isOtherCompositionBucket(cell.id)) {
      group.cells.push({ id: cell.id, name: cell.label, count, total });
    }
  }
  return [...groups.values()];
};

const viewOf = (
  table: TableData,
  rows: readonly number[],
  [level, sublevel]: readonly [TableDimension, TableDimension | undefined],
  root: IcicleRoot
): IcicleView => {
  const groups = groupsOf(table, rows, level, sublevel);
  if (root.level === undefined || isSublevelOf(level, root.level)) {
    return {
      rootColumn: undefined,
      groups,
      groupLevel: level,
      cellLevel: sublevel,
    };
  }
  const self =
    level === root.level
      ? groups.find((group) => group.id === root.id)
      : undefined;
  return {
    rootColumn: self && { cell: self, level },
    groups: (self?.cells ?? []).map((cell) => ({ ...cell, cells: [] })),
    groupLevel: sublevel,
    cellLevel: undefined,
  };
};

const populationOf = (groups: readonly IcicleCell[]): number => {
  const sized = groups.find((group) => group.total > 0);
  return sized ? Math.round(sized.count / sized.total) : 0;
};

const uniqueColumn = (label: string, taken: Set<string>): string => {
  let column = label;
  for (let suffix = 2; taken.has(column); suffix++) {
    column = `${label} (${suffix})`;
  }
  taken.add(column);
  return column;
};

export const buildTableIcicle = ({
  table,
  entities,
  drilledInto,
  drillPath,
  overlapping,
  coverage,
  geographySelectionListIds,
  entityLabel,
}: {
  table: TableData;
  entities: readonly TableIcicleEntity[];
  drilledInto: IcicleRoot | undefined;
  drillPath: readonly IcicleDrillStep[];
  overlapping: boolean;
  coverage: TreemapTaxonomyCoverage | undefined;
  geographySelectionListIds: readonly string[];
  entityLabel: string;
}): IcicleData => {
  const [entityDimension, level, sublevel] = table.dimensions;
  const nodes: IcicleNode[] = [];
  const meta = new Map<string, IcicleNodeMeta>();
  if (
    entityDimension === undefined ||
    level === undefined ||
    !isLevelDimension(level)
  ) {
    return { icicle: { nodes, meta }, exportData: null };
  }

  const maxGroups = taxonomyCoverageMaxGroups(coverage) ?? MAX_PARENT_GROUPS;
  const maxCells = coverage?.topN?.cells ?? DEFAULT_MAX_CELLS;
  const omitOther = coverage?.omitOther === true;
  const showEntities = entities.length > 1;
  const rowsOf = new Map(
    groupTableRows(table, entityDimension).map(({ value, rows }) => [
      value.id,
      rows,
    ])
  );
  const ladderIdOf = (dimension: TableDimension | undefined) =>
    (dimension && findLadderLevel(dimension, geographySelectionListIds)?.id) ??
    '';
  const canDrill = (dimension: TableDimension) =>
    isLevelDimension(dimension) &&
    getDrilldownBreakdownLevel(dimension, geographySelectionListIds) !==
      undefined;

  const weigh = (
    parent: Remainder,
    all: readonly IcicleCell[],
    kept: readonly IcicleCell[]
  ): { weights: Map<IcicleCell, number>; other: Remainder | null } => {
    const keptTotal = sumTotals(kept);
    const allTotal = sumTotals(all);
    const denominator = (() => {
      if (omitOther) return keptTotal;
      return overlapping ? allTotal : Math.max(parent.total, allTotal);
    })();
    const widthOf = (total: number) =>
      denominator > 0 ? (parent.weight * total) / denominator : 0;
    return {
      weights: new Map(kept.map((cell) => [cell, widthOf(cell.total)])),
      other: omitOther
        ? null
        : {
            weight: parent.weight - widthOf(keptTotal),
            total: (overlapping ? allTotal : parent.total) - keptTotal,
            count:
              (overlapping ? sumCounts(all) : parent.count) - sumCounts(kept),
          },
    };
  };

  for (const entity of entities) {
    const rows = rowsOf.get(entity.id);
    if (rows === undefined) continue;
    const view = viewOf(table, rows, [level, sublevel], {
      level: drilledInto?.level ?? entity.level,
      id: drilledInto?.id ?? entity.id,
    });
    if (view.rootColumn === undefined && view.groups.length === 0) continue;

    const prefix = showEntities ? `${entity.id}:` : '';
    const entityNodeId = showEntities ? `entity:${entity.id}` : null;
    const entityNodes: IcicleNode[] = [];

    const emit = (
      cell: IcicleCell,
      dimension: TableDimension,
      parent: { id: string | null; path: readonly IcicleDrillStep[] },
      weight: number,
      drillable = true
    ) => {
      const step = {
        levelId: ladderIdOf(dimension),
        id: cell.id,
        name: cell.name,
      };
      const id = `${prefix}${step.levelId}:${cell.id}`;
      const path = [...parent.path, step];
      entityNodes.push({ id, parentId: parent.id, name: cell.name, weight });
      const drillPath = (() => {
        if (!drillable) return null;
        if (canDrill(dimension)) return path;
        return parent.path.length > 0 ? parent.path : null;
      })();
      meta.set(id, {
        entityKey: entity.id,
        total: cell.total,
        count: cell.count,
        isEntity: false,
        path,
        drillPath,
      });
      return { id, path, weight };
    };

    const emitOther = (
      id: string,
      parent: { id: string | null; path: readonly IcicleDrillStep[] },
      dimension: TableDimension | undefined,
      other: Remainder
    ) => {
      entityNodes.push({
        id,
        parentId: parent.id,
        name: 'Other',
        weight: other.weight,
        isOther: true,
      });
      meta.set(id, {
        entityKey: entity.id,
        total: other.total,
        count: other.count,
        isEntity: false,
        path: [
          ...parent.path,
          { levelId: ladderIdOf(dimension), id: 'other', name: 'Other' },
        ],
        drillPath: null,
      });
    };

    const root: Remainder = {
      weight: 1,
      total: view.rootColumn?.cell.total ?? 1,
      count: view.rootColumn?.cell.count ?? populationOf(view.groups),
    };
    const rootColumn =
      view.rootColumn &&
      emit(
        view.rootColumn.cell,
        view.rootColumn.level,
        { id: entityNodeId, path: [] },
        root.weight,
        false
      );
    const groupParent = { id: rootColumn?.id ?? entityNodeId, path: [] };

    const ranked = [...view.groups].sort(byTotal);
    const topGroups = ranked.slice(0, maxGroups);
    const keptCells = new Set(
      topGroups
        .flatMap((group) => group.cells)
        .sort(byTotal)
        .slice(0, maxCells)
    );
    const cellsOf = (group: IcicleGroup) =>
      group.cells.filter((cell) => keptCells.has(cell)).sort(byTotal);
    const groups = topGroups.filter(
      (group) =>
        group.cells.length === 0 || cellsOf(group).length > 0 || !omitOther
    );

    const { groupLevel, cellLevel } = view;
    const top = weigh(root, ranked, groups);
    if (groupLevel !== undefined) {
      for (const group of groups) {
        const groupNode = emit(
          group,
          groupLevel,
          groupParent,
          top.weights.get(group) ?? 0
        );
        if (cellLevel === undefined || group.cells.length === 0) continue;
        const cells = cellsOf(group);
        const { weights, other } = weigh(
          { weight: groupNode.weight, total: group.total, count: group.count },
          group.cells,
          cells
        );
        for (const cell of cells) {
          emit(cell, cellLevel, groupNode, weights.get(cell) ?? 0);
        }
        if (other && hasRemainder(other.weight, groupNode.weight)) {
          emitOther(`${groupNode.id}:other`, groupNode, cellLevel, other);
        }
      }
      if (top.other && hasRemainder(top.other.weight, root.weight)) {
        emitOther(`${prefix}other`, groupParent, groupLevel, top.other);
      }
    }

    if (entityNodeId !== null || entityNodes.length === 0) {
      const id = entityNodeId ?? `entity:${entity.id}`;
      nodes.push({ id, parentId: null, name: entity.name, weight: 1 });
      meta.set(id, {
        entityKey: entity.id,
        total: 1,
        count: root.count,
        isEntity: true,
        path: [],
        drillPath: null,
      });
    }
    nodes.push(...entityNodes);
  }

  const icicle = {
    nodes: drilledInto === undefined ? promoteLoneTopLevelNode(nodes) : nodes,
    meta,
  };
  return {
    icicle,
    exportData: exportDataOf({
      icicle,
      entities,
      drillPath,
      taxonomyLevel: level,
      geographySelectionListIds,
      entityLabel,
    }),
  };
};

const exportDataOf = ({
  icicle: { nodes, meta },
  entities,
  drillPath,
  taxonomyLevel,
  geographySelectionListIds,
  entityLabel,
}: {
  icicle: IcicleData['icicle'];
  entities: readonly TableIcicleEntity[];
  drillPath: readonly IcicleDrillStep[];
  taxonomyLevel: LevelDimension;
  geographySelectionListIds: readonly string[];
  entityLabel: string;
}): IcicleData['exportData'] => {
  const ladder =
    taxonomyOf(taxonomyOfLevel(taxonomyLevel), geographySelectionListIds)
      ?.ladder ?? [];
  const entityByKey = new Map(entities.map((entity) => [entity.id, entity]));
  const cells = nodes.flatMap((node) => {
    const nodeMeta = meta.get(node.id);
    const entity = nodeMeta && entityByKey.get(nodeMeta.entityKey);
    if (nodeMeta === undefined || entity === undefined) return [];
    const names = new Map<string, string>();
    for (const step of [...drillPath, ...nodeMeta.path]) {
      names.set(step.levelId, step.name);
    }
    return [{ entity, nodeMeta, names }];
  });
  if (cells.length === 0) return null;

  const taken = new Set<string>();
  const entityColumn = uniqueColumn(entityLabel, taken);
  const levelColumns = ladder
    .filter(({ id }) => cells.some(({ names }) => names.has(id)))
    .map(({ id, level }): readonly [string, string] => [
      id,
      uniqueColumn(formatColumnName(level), taken),
    ]);
  const countColumn = uniqueColumn('Count', taken);
  const shareColumn = uniqueColumn('Share', taken);

  return {
    type: DataType.TABLE_ROWS,
    data: cells.map(({ entity, nodeMeta, names }) => {
      const row: Record<string, string | number> = {
        [entityColumn]: entity.name,
      };
      for (const [levelId, column] of levelColumns) {
        row[column] = names.get(levelId) ?? '';
      }
      row[countColumn] = nodeMeta.count;
      row[shareColumn] = nodeMeta.total;
      return row;
    }),
  };
};
